import { cn } from '@/lib/utils'
import { toAccountProbe, toVendorStatus } from '@/lib/sendgrid-data'
import type { VendorStatusPayload } from '@/lib/api/deep-dive.types'
import { PanelError } from './PanelState'

const toneClass = {
  healthy: 'text-healthy',
  degraded: 'text-degraded',
  critical: 'text-critical',
  neutral: 'text-fg',
} as const

interface VendorStatusCardsProps {
  payload: VendorStatusPayload | undefined
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
}

/**
 * The two halves of `{vendor}/status`: what the vendor says about itself, and
 * what our own credentials got when we called it.
 *
 * They are two cards rather than one because they fail apart and mean different
 * things. The vendor's Statuspage severity is the whole internet's view and
 * nothing we can act on; the account probe is this deployment's own key against
 * their API, and is the half an on-call engineer can actually fix. Collapsing
 * them would let a green vendor hide a rejected key of ours, which is the exact
 * failure this screen exists to catch.
 *
 * Each card prints what it checked underneath — the vendor's own wording, the
 * endpoint that was called — so the reader can compare this screen against the
 * vendor's console without guessing what was measured.
 */
export function VendorStatusCards({ payload, isLoading, error, onRetry }: VendorStatusCardsProps) {
  if (error) {
    return (
      <PanelError
        error={error}
        onRetry={onRetry}
        className="border-line bg-surface rounded-lg border"
      />
    )
  }

  const vendor = toVendorStatus(payload)
  const probe = toAccountProbe(payload?.account)

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card
        label="Vendor status"
        value={isLoading ? '…' : vendor.word}
        tone={vendor.tone}
        caption={vendor.detail}
        footer={
          payload?.response_time_ms != null
            ? `Status check · ${payload.response_time_ms}ms`
            : 'Status check'
        }
      />

      {/* Absent rather than empty when the payload carries no probe: a card
          reading "n/a" would suggest the credentials failed a check that was
          never run. */}
      {probe && (
        <Card
          label="Our account"
          value={probe.value}
          tone={probe.tone}
          caption={probe.caption}
          footer={
            probe.missingEnv?.length
              ? `Missing: ${probe.missingEnv.join(', ')}`
              : probe.endpoint
                ? `Account probe · ${probe.endpoint}`
                : 'Account probe'
          }
        />
      )}
    </div>
  )
}

function Card({
  label,
  value,
  tone,
  caption,
  footer,
}: {
  label: string
  value: string
  tone: keyof typeof toneClass
  caption: string
  footer: string
}) {
  return (
    <div className="border-line bg-surface rounded-lg border p-5">
      <p className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
        {label}
      </p>
      <p className={cn('mt-4 font-mono text-3xl leading-none font-bold', toneClass[tone])}>
        {value}
      </p>
      <p className="text-fg mt-3 text-sm">{caption}</p>
      {/* The receipt for the figure above: what was called, or what is missing.
          Truncated rather than wrapped — a probe URL is long and this is a
          reference, not a headline. */}
      <p title={footer} className="text-fg-subtle mt-2 truncate font-mono text-[11px]">
        {footer}
      </p>
    </div>
  )
}
